// SeriesTileBlocks — the series-bound blocks for the composable series-picker
// tiles, plus the SeriesContext that carries the "current series" down to them.
// This is the series-picker analogue of RowContext + ProductRowBlocks: the
// picker renders the series-picker-tiles slot once per series, wrapping each
// tile's blocks in a <SeriesContext.Provider> bound to that series, and these
// blocks read it back via useCurrentSeries(). Outside a tile the context is
// null, so a stray series-* block placed in a normal slot renders nothing.

import { createContext, useContext } from "react";
import type { DesignerSeriesDto } from "../types";
import type { BlockUniversalProps } from "./types";

/** Null outside a series tile. */
export const SeriesContext = createContext<DesignerSeriesDto | null>(null);

/** Read the current series. Null when not rendered inside a series tile — the
 *  series-* blocks treat that as "render nothing". */
export function useCurrentSeries(): DesignerSeriesDto | null {
  return useContext(SeriesContext);
}

/** The current series' hero image (falls back to its icon). Renders an empty
 *  placeholder when the series has neither, so the tile keeps its image area. */
export function SeriesImageBlock({ themeVariant, className }: BlockUniversalProps) {
  const series = useCurrentSeries();
  if (!series) return null;
  const img = series.heroImageUrl ?? series.iconUrl ?? null;
  return (
    <div
      className={"sd-eg-series__tile-img" + (className ? " " + className : "")}
      data-variant={themeVariant}
    >
      {img ? (
        <img src={img} alt="" draggable={false} />
      ) : (
        <div className="sd-eg-series__tile-img-empty" />
      )}
    </div>
  );
}

export interface SeriesNameBlockProps extends BlockUniversalProps {
  /** Visual variant hook (mirrors TextBlock's variants) so the operator can
   *  size the name. Applied as a data attribute the CSS can target. */
  variant?: string;
}

/** The current series' name (label). */
export function SeriesNameBlock({ variant, themeVariant, className }: SeriesNameBlockProps) {
  const series = useCurrentSeries();
  if (!series?.label) return null;
  return (
    <span
      className={"sd-eg-series__tile-name" + (className ? " " + className : "")}
      data-variant={themeVariant}
      data-text-variant={variant}
    >
      {series.label}
    </span>
  );
}

/** The current series' description. Renders nothing when the series has none. */
export function SeriesDescriptionBlock({ variant, themeVariant, className }: SeriesNameBlockProps) {
  const series = useCurrentSeries();
  if (!series?.description) return null;
  return (
    <span
      className={"sd-eg-series__tile-description" + (className ? " " + className : "")}
      data-variant={themeVariant}
      data-text-variant={variant}
    >
      {series.description}
    </span>
  );
}
